/**
 * Firestore `views/` read-model for the requirements-assessment track (MYAC-930).
 *
 * These interfaces are HAND-WRITTEN MIRRORS of the CMS view models (the client
 * data contract, `work/2026-07-29-rest-api-doctrine/spec.md` §4 + §4.6b) — the
 * view schema IS the contract, so this file is the front's half of it and must be
 * kept in step with `core_models_services` `requirements_assessment/views`.
 *
 * They are plain interfaces on purpose: view docs are projected read-models, not
 * Firestore *entities*, so no class-transformer / `@Transform` layer applies —
 * the front never writes them (rules: `allow write: if false`) and never
 * round-trips them.
 *
 * SCHEMA v2 (§4.6b, live on dev — `view_version: 2` on nav / items / remediations
 * / task_groups / clarifications, `assessment_continuity` NEW at v1). The reuse
 * directive (§12.25) is what shapes this file: **views speak the front's existing
 * component vocabulary**, so every `by_rating` is the `Ratings` shape
 * `{A,B,C,D,F,TOTAL}` that `RatingBadges` renders, and the theme blocks carry the
 * field names `ThemesBand` renders. There is no adapter layer any more — the
 * types below ARE what the components consume.
 *
 * Two evolution rules the readers below rely on (spec §4.0-4):
 *  - **Additive writers / tolerant readers.** Optionality here is not cosmetic,
 *    it encodes the governing rule of §4.6b: an explicit `0` means *computed and
 *    empty*, an ABSENT field means *not computed at this level*. So a counter the
 *    projection always computes is required (`by_rating`, `na`, `expected`…), and
 *    only the genuinely level-dependent ones are optional (`mac_score`,
 *    `periodic_objects`, whole chapter-level theme blocks).
 *  - `view_version` is bumped only for a breaking reshape.
 */

import type { DocumentHandle } from '@/models/DocumentHandle'
import type { Timestamp } from 'firebase/firestore'
import type { RatingCounts } from '@/models/Assessment'
import type { ScopeTrack } from '@/models/requirementsAssessment'

/** The A–F letter grades the engine emits. N/A is a *status*, not a rating. */
export type ViewRating = 'A' | 'B' | 'C' | 'D' | 'F'

/**
 * A rating distribution: `{A,B,C,D,F,TOTAL}` with EXPLICIT zeros (§12.25) — the
 * exact shape `RatingBadges` consumes, so a view field is passed to the component
 * with no mapping. `TOTAL` is the number of counted verdicts (`sum(A..F)`).
 */
export type { RatingCounts }

/** The 4C partition (spec §4.6 "theme derivation"). Items are never `consistency`. */
export type ViewTheme = 'compliance' | 'completeness' | 'continuity' | 'consistency'

/** Item lifecycle in the assessment (N/A items carry no verdict). */
export type ViewItemStatus = 'open' | 'not_applicable'

/** The display lens of an adoption — derived from its declared stage. */
export type ViewLens = 'stage1' | 'stage2'

/**
 * What the engine is DOING right now, in the user's words (MYAC-968).
 *
 * A `lens` in the platform-modeling §2 sense: the server projects its closed
 * 19-value action vocabulary onto these seven labels, so the browser never learns
 * that `do_ground` exists. Mirrors `AssessmentPhase` in CMS `views.py` — the
 * order matters only to readers, never to code.
 *
 * `unknown` is the tolerant-reader arm: a server that grows a new action this
 * build has no label for sends `unknown` rather than a raw action name, and the
 * front renders its generic "working" label. Any other unexpected string is
 * treated the same way at the store boundary.
 */
export type AssessmentPhase =
  | 'preparing'
  | 'reading'
  | 'planning'
  | 'assessing'
  | 'verifying'
  /** Post-run consistency pass — NOT a ladder step: renders on the verifying
   *  dot with a dedicated title and its own history line. */
  | 'consistency'
  | 'tasks'
  | 'summarizing'
  | 'unknown'

// ---------------------------------------------------------------------------
// 4.1 — assessment_status (hot singleton: engine runtime state, nothing else)
// ---------------------------------------------------------------------------

/** One unit of work genuinely in flight (MYAC-969) — a document being read, a
 * requirement being rated. One entry per concurrent worker (≤4), so the list is
 * also the honest picture of parallelism. */
export interface ActiveUnit {
  /** What `label` names. The kinds the engine writes; a union rather than a bare
   *  string so a renamed kind fails at compile time instead of silently never
   *  matching (same treatment as `RunScope.kind`). `registry` is the external
   *  registry refresh (EUDAMED) — network time that must not masquerade as
   *  document reading. `analysis` is corpus comprehension (grammar, census,
   *  record map, scope facts) — READING-phase work on documents already
   *  fetched, named so the title can stay stable while a sub-text says it. */
  kind: 'document' | 'requirement' | 'chunk' | 'registry' | 'analysis'
  /** A filename or a requirement code — already client-facing vocabulary. */
  label: string
  /** i18n key SUFFIX (`referentials.progress.unit.*`) — never rendered raw. */
  detail?: string
  since?: Timestamp
}

/** One unit just finished — the scrollable "what it just read" tail (MYAC-969). */
export interface RecentUnit {
  label: string
  at?: Timestamp
}

/** One phase transition (MYAC-969). A mark is a START: its duration runs to the
 * next mark's `at` (or, for a sealed run's last mark, to `finished_at`).
 * `summary` is the engine's own closing words for the phase ("planned 29 items…")
 * — AUDIT text since 19/08, no longer displayed; the client renders `note`. */
export interface PhaseMark {
  phase?: AssessmentPhase
  at?: Timestamp
  summary?: string
  /** Client-facing closing note (décision Charles 19/08): closed {key, counts}
   *  vocabulary, rendered localized — absent → label + duration only. */
  note?: HistoryNote
}

/** The closed note vocabulary — tolerant reader: an unknown key renders nothing. */
export interface HistoryNote {
  key?: string
  [count: string]: string | number | undefined
}

/** WHAT the run was launched over (MYAC-969) — full referential vs a scoped subset. */
export interface RunScope {
  kind: 'full' | 'partial'
  /**
   * WHY the run started, which `kind` cannot say: a replan triggered by an
   * answer covers the whole referential and so reads as `full`. Absent on an
   * older server (and on paths the engine cannot attribute) — render no badge
   * rather than guessing, and keep this a plain string so a value added later
   * degrades to "no badge" instead of failing to compile.
   */
  origin?: string
  uid_count?: number
  /** Sub-chapter keys the scoped uids belong to — what the tree highlights. */
  sub_keys?: string[]
  /** The scoped requirements' codes, small runs only — "re-ran 7.5.6". */
  codes?: string[]
}

/** The sealed report of the last finished run (MYAC-969) — persists until the
 * next run replaces it, so the graph can stay up as "what did it actually do". */
export interface RunReport {
  started_at?: Timestamp
  finished_at?: Timestamp
  phases?: PhaseMark[]
  totals?: {
    requirements_rated?: number
    documents_read?: number
    tasks_generated?: number
    groups?: number
  }
  run_scope?: RunScope
}

/**
 * The live-progress block (MYAC-969) — shared by the per-adoption rows and the
 * scope row. Every field optional BY CONTRACT: an older server writes none of
 * them and the UI degrades to the MYAC-968 phase line, never crashes.
 */
export interface ProgressFields {
  phase?: AssessmentPhase
  /** Sub-progress within the phase; both absent ⇒ indeterminate (show activity,
   * never a fake fraction). */
  phase_done?: number | null
  phase_total?: number | null
  batch?: string | null
  /** Pass number of a repeating phase (task generation runs several rounds) —
   * ≥2 explains a counter that legitimately rewinds. */
  phase_pass?: number | null
  active_units?: ActiveUnit[]
  recent_units?: RecentUnit[]
  phase_history?: PhaseMark[]
  run_scope?: RunScope | null
  last_run?: RunReport | null
}

/** The waiting banner's data (MYAC-1052 legacy parity) — present ONLY while the
 * run is parked waiting for the corpus (documents syncing/ingesting). The
 * engine resumes it by itself; the user has nothing to do. `total_documents`
 * is frozen at park time, `remaining_documents` is refreshed as ingestion
 * advances — derive "N/M documents" or a fraction from the pair (guard against
 * a zero total: older servers or an instant park may write none). */
export interface AdoptionWaitingInfo {
  reason?: string
  total_documents?: number
  remaining_documents?: number
  since?: Timestamp
  updated_at?: Timestamp
}

/**
 * WHICH KIND of wait `awaiting_human` names (S2) — the NORMALIZED vocabulary,
 * what `awaitKindOf()` returns and what the banner branches on.
 *
 *  - `questions` — a clarification is open: the questions surface owes the answer.
 *  - `relaunch`  — a clean engine yield (the wake segment spent its time budget):
 *                  nothing is broken, the platform's watchdog resumes it.
 *  - `incident`  — parked by neither: a crash, a reaper park, a watchdog re-park.
 *
 * There is no `''` member on purpose: the value is only read on a row already
 * known to be parked, and "parked, and this build cannot say why" is an
 * incident — the honest catch-all, never a soothing one.
 */
export type AwaitKind = 'questions' | 'relaunch' | 'incident'

/**
 * One human ACT posted on an adoption and not folded by the engine yet
 * (MYAC-1018) — a letter still in the mailbox.
 *
 * This is the fact the browser used to read straight off `agents_blackboard`
 * (`kind == "event", status == "pending"`) until that read rule flipped to
 * `false` on 2026-08-10, taking the buffer pill and the cross-tab
 * « Désadoption… » badge silently down with it. Same shape, legal source.
 *
 * NOT `queue_position`, which answers a different question: that one says where
 * this referential's launch sits in the run queue, this one says what is
 * stacked on it.
 */
export interface PendingActRow {
  /**
   * Board event type — `clarification_answer`, `referential_removed`, …
   *
   * A plain `string`, NOT a union, and deliberately so: the server sends its own
   * vocabulary and a NEWER member must degrade to the generic label rather than
   * fall through every arm. Label it through the pill's `te()` guard.
   */
  type: string
  /** When it was posted. Firestore hands back a `Timestamp`; cached reads and
   *  tests hand back a `Date`. Read it through the store's `toDate`. */
  at?: Timestamp | Date | null
}

export interface AdoptionRuntimeStatus extends ProgressFields {
  lifecycle_state: string
  busy: boolean
  /** WHY the engine parked — only meaningful when lifecycle is awaiting_human. */
  await_rationale?: string
  /**
   * S2 — which kind of wait `await_rationale` describes (`''` when not awaiting).
   *
   * A plain `string`, NOT the `AwaitKind` union, and that is the contract: the
   * server's own reader degrades an unknown member to `incident`, so this one
   * must be able to receive one. Read it through `awaitKindOf()` — never
   * compared raw, or a value added later would fall through every arm.
   */
  await_kind?: string
  /** MYAC-1052 — set while the run waits for the corpus; absent otherwise. */
  waiting?: AdoptionWaitingInfo | null
  /**
   * MYAC-1018 — the unfolded mailbox, OLDEST FIRST (the order the engine will
   * fold them, so no client ever re-sorts). Absent on a server that predates
   * the field; empty is the common case.
   */
  pending_acts?: PendingActRow[]
}

/** Progress of the work that belongs to the SCOPE (MYAC-969): grounding reads the
 * corpus ONCE for every adopted referential, and the executive summary is one pass
 * too. Which phases are scope-level is engine knowledge — this row is how the
 * client learns it without learning the engine's vocabulary. */
export type ScopeRuntimeStatus = ProgressFields

/**
 * THE row the page header paints (S2/B4) — label and fraction from ONE place.
 *
 * The header shows a phase label next to a fraction and had no single source for
 * the pair: the label came from wherever the client looked first (the scope row,
 * an adoption row) while the numbers came from somewhere else. Live 2026-08-18:
 * « Lecture de vos documents 2057/2057 » stood in the header, painted from a
 * scope row whose reading had ended hours earlier, while the adoption row was
 * assessing — two rows, two phases, the header listening to the wrong one.
 *
 * The ENGINE picks the row (only it knows what is in flight) and the four fields
 * are read TOGETHER or not at all. `null` = nothing is running; a client that
 * predates the field keeps deriving the pair from the rows as before.
 */
export interface HeadlineRow {
  /** `'scope'` or an `adoption_id` — the row these numbers came from. */
  row: string
  phase?: AssessmentPhase | null
  /** That row's `phase_done`/`phase_total` — absent when the phase has no
   *  honestly countable unit (never a fabricated fraction). */
  done?: number | null
  total?: number | null
}

export interface AssessmentStatusView {
  view_version: number
  /** adoption_id → runtime state. */
  adoptions: Record<string, AdoptionRuntimeStatus>
  /** Scope-level progress (MYAC-969) — absent until a scope-subject recipe runs. */
  scope?: ScopeRuntimeStatus
  /** The header's single source (S2/B4). `null`/absent → derive from the rows. */
  headline?: HeadlineRow | null
  updated_at?: Timestamp
  /** The runner job's 60 s liveness beat (MYAC-1006) — moves DURING long worker
   * calls, unlike `updated_at` which only moves at action boundaries. Absent
   * until the first post-deploy wake; the projection never writes it. */
  wake_heartbeat_at?: Timestamp
  /**
   * MYAC-1433 / MYAC-1439 — ONE run row per track (`qms`, `td`), the screen's
   * one source for the run's state. ABSENT on a project that never ran on the
   * engine that writes it, and absent per track: an absent row reads as `idle`.
   */
  run?: Record<string, RunRow>
}

/**
 * The client vocabulary of a track's run (CMS `RunRowState`). A plain string on
 * the wire — a value this build does not know is read as `running` by
 * `normalizeRunState` (the enum's own `_missing_` rule), never as `idle`.
 */
export type RunRowState =
  | 'idle'
  | 'starting'
  | 'running'
  | 'queued_behind_corpus'
  | 'waiting_human'
  | 'yielded'
  | 'aborted'
  | 'dead'

/** The counters of the run's CURRENT step — reset at every phase entry, so a
 * step without counters carries none (never the previous step's). */
export interface RunProgressRow {
  done?: number | null
  total?: number | null
  /** Engine-internal sweep position — never rendered (see `fractionWithBatch`). */
  batch?: string | null
}

/** How the last run of the track ended — sealed ONCE, at the run's exit. */
export interface RunLastRow {
  started_at?: Timestamp | null
  finished_at?: Timestamp | null
  /** Since when the track is ASSESSED: the oldest referential's end in that run
   *  (Charles, 2026-09-23) — absent on an entry that predates the windows. */
  assessed_at?: Timestamp | null
  outcome?: string
  head?: string
}

/** One thing the run serves next (CMS `RunQueueItem`): `owed` = launched and
 * owing, served by the running wake itself; `queued` = its command waits in the
 * track's queue. `kind` says WHOSE it is (MYAC-1439, incident row 45): a
 * `referential` item belongs to its `adoption_id`; a `track` item (the corpus
 * consistency pass, `type: 'consistency'`) belongs to the whole track, carries
 * `adoption_id: ''` and is never attributed to a referential row. `kind` absent
 * ⇒ `referential` (an older projection). `source` / `kind` / `type` stay plain
 * strings: a value added later must not fail to compile. */
export interface RunQueueItem {
  adoption_id: string
  command_id?: string
  source?: string
  kind?: string
  /** The command a `track` item runs — `consistency` for the pass. */
  type?: string
}

/**
 * `views/assessment_status.run[track]` (CMS `RunRow`) — the image of the
 * engine's `run` entry plus the handler's phase. The screen reads it and
 * derives nothing: `state` gives the word, the two facets `live` /
 * `needs_user` the behaviour, `phase` + `progress` the popover, `queue` the
 * positions, `last` the « Évaluée il y a … » line, `heartbeat_at` the one
 * staleness input. Read through `normalizeRunRow`.
 */
export interface RunRow {
  state?: string
  /** The engine works or will work — « Arrêter » shown, relaunches blocked. */
  live?: boolean
  /** Nothing moves without the user — the situation card. */
  needs_user?: boolean
  since?: Timestamp | null
  command_id?: string
  /** The adoption the walker serves now ('' when idle). */
  head?: string
  /** What it serves next, in order (MYAC-1439: boxed items; an older
   *  projection sent bare adoption ids — `normalizeRunRow` boxes them). */
  queue?: Array<RunQueueItem | string>
  phase?: string | null
  progress?: RunProgressRow | null
  last?: RunLastRow | null
  heartbeat_at?: Timestamp | null
  /** The run entry's id — what an item row's `judged_run_id` compares to. */
  entry_id?: string
}

/** The canonical display order of the closed phase vocabulary — what "least
 * advanced" and past/present/future derivations key on. `unknown` sorts last so a
 * new server-side phase never claims to be earlier than work already shown.
 * Reading precedes planning since MYAC-1027 Phase 1 (reading-first engine,
 * 14/08): the corpus is read before the profile/plan — the old order painted
 * the "Planification" dot as past during the long reading phase. */
export const PHASE_ORDER: readonly AssessmentPhase[] = [
  'preparing',
  'reading',
  'planning',
  'assessing',
  'verifying',
  'consistency',
  'tasks',
  'summarizing',
  'unknown',
] as const

/**
 * The phases that HAVE a label (`referentials.progress.phase.*`) — PHASE_ORDER
 * minus `unknown`, the tolerant-reader arm that renders the generic line.
 *
 * DERIVED, never re-listed (S3/6). The front carried three hand-written copies
 * of this vocabulary and two of them had drifted: the store's listed `planning`
 * before `reading`, which is the wrong pipeline order since MYAC-1027 and would
 * have made a reading run read as "less advanced" than a planning one in the
 * header's least-advanced fold. One source, so a phase added server-side is
 * added here and nowhere else.
 */
export const NAMED_PHASES: readonly AssessmentPhase[] = PHASE_ORDER.filter((p) => p !== 'unknown')

// ---------------------------------------------------------------------------
// 4.1b — assessment_clarifications/rows/{requestId}
// ---------------------------------------------------------------------------

export type ViewClarificationStatus = 'pending' | 'answered'

export interface ClarificationOption {
  label: string
  /** What picking this answer implies (shown under the option). */
  consequence?: string
}

export interface AssessmentClarificationRow {
  view_version: number
  /**
   * v2 (§4.6b): OFFICIALLY OPTIONAL — `''` on a scope-level question (a device
   * fact such as "is it sterile?" owns no adoption), and backfilled only when the
   * scope resolves to exactly one active adoption. Never the addressing field.
   */
  adoption_id: string
  /** v2: the addressing field — ALWAYS set, and what the per-track banner filters on. */
  scope_track: string
  /** The adoption whose run RAISED a scope-level question (19/08) — the per-row
   *  chip filters on it; empty on rows written before the field existed. */
  raised_by?: string
  /** IR-165 — the profile (registry) key this question settles, e.g.
   *  `supply_chain.downstream_repackaging`; `''`/absent on a free question and
   *  on rows projected before the field existed. */
  field?: string
  status: ViewClarificationStatus
  question: string
  options: ClarificationOption[]
  /** The orchestrator's suggested answer, when it has one. */
  recommended?: string
  created_at?: Timestamp
  answered_at?: Timestamp | null
}

// ---------------------------------------------------------------------------
// 4.2 — assessment_nav (the whole left panel + every counter, ONE doc)
// ---------------------------------------------------------------------------

/**
 * The compliance partition: items that are neither presence nor periodic.
 *
 * `mac_score` is the score of THIS distribution, NOT the adoption's overall one
 * (§12.18) — pairing the overall score with the partition distribution was
 * incoherent on TD, and is now measurably off on QMS too (§4.6b widens §12 note
 * 8: 90.55 vs 91.51 on iso_13485). `null`/absent = nothing rated in the partition.
 */
export interface NavComplianceTheme {
  by_rating: RatingCounts
  na: number
  mac_score?: number | null
}

/**
 * The evidence-dossier partition (presence items), DOCUMENT/radar axis only
 * (§12.10 DECIDED): `present + to_attach + to_produce === expected`. The
 * criteria axis is deliberately gone — its granularity lives in the presence
 * items' own ratings and per-item `criteria_status`.
 */
export interface NavCompletenessTheme {
  by_rating: RatingCounts
  present: number
  expected: number
  to_attach: number
  to_produce: number
}

/**
 * The periodic partition. `periodic_objects` counts the scope's derived periodic
 * OBLIGATIONS — document-anchored, hence adoption-level ONLY: absent (not `0`) on
 * a chapter block.
 */
export interface NavContinuityTheme {
  by_rating: RatingCounts
  cadence_undefined: number
  periodic_objects?: number
}

/** Tasks only — no `by_rating`. Cross-document, hence adoption-level ONLY. */
export interface NavConsistencyTheme {
  open: number
  resolved: number
  /**
   * How many named properties the sweep actually COMPARED across the corpus
   * (MYAC-890's coverage register, projected since then and read here since
   * 2026-08-26).
   *
   * It is what separates «swept, nothing to report» from «never swept» — two
   * states the tab used to render with the same sentence. Zero/absent on a
   * scope that never ran the reconciliation, and on legacy sweeps that predate
   * the register (those still have their findings to prove they ran).
   *
   * CORPUS-WIDE: the sweep covers the whole track, so every adoption of a
   * track carries the SAME number. Read one, never sum them.
   */
  properties_checked?: number
}

/**
 * The 4C partition block.
 *
 * ADOPTION level (§12.18): ALWAYS all four sub-blocks, zero-valued when computed
 * and empty — "0 open findings" must be distinguishable from "not computed".
 * CHAPTER level: SPARSE — a block appears only where attribution is real, and
 * `consistency` never appears. Hence every sub-block is optional here.
 */
export interface NavThemes {
  compliance?: NavComplianceTheme
  completeness?: NavCompletenessTheme
  continuity?: NavContinuityTheme
  consistency?: NavConsistencyTheme
}

/** MYAC-1688 — a tree node's requirements by checklist state, counted by the
 * backend (`grading.checklist_state`). Absent on a projection that predates it:
 * the tree then shows no marker rather than zeros. */
export interface ChecklistCounts {
  complete: number
  partial: number
  missing: number
  na: number
  not_evaluated: number
}

/** One requirement's checklist state, as the backend projects it on its row. */
export type ChecklistState = 'complete' | 'partial' | 'missing' | 'na' | 'not_evaluated' | 'unknown'

export interface NavSubNode {
  /** Oldest current verdict of the section's items — the re-run tooltip's date. */
  oldest_judged_at?: Timestamp
  code: string
  /** Explicit items-query key — the front never composes it (spec §4.2). */
  sub_key: string
  title: string
  by_rating: RatingCounts
  na: number
  /** MYAC-969: section-grain denominator (live items minus N/A) — `by_rating.TOTAL`
   * is its numerator. Optional: absent on pre-969 docs, fall back to counts. */
  assessable_total?: number
  checklist?: ChecklistCounts | null
}

export interface NavChapterNode {
  oldest_judged_at?: Timestamp
  code: string
  title: string
  by_rating: RatingCounts
  na: number
  /** SPARSE — only the theme blocks with real attribution at this chapter. */
  themes?: NavThemes
  subs?: NavSubNode[]
  checklist?: ChecklistCounts | null
}

export interface NavAdoption {
  /** Oldest current verdict across the adoption — referential-level tooltip date. */
  oldest_judged_at?: Timestamp
  adoption_id: string
  referential_id: string
  /** Client-facing name — kills the front's `formatReferential()` derivation. */
  display_name: string
  revision_label: string
  scope_track: ScopeTrack
  /** Declared certification stage: 'stage1' | 'stage2' | '' (TD is always ''). */
  mode: string
  /** The server is the only scorer. */
  mac_score?: number | null
  /** OVERALL distribution at the effective lens — ALL items, every theme. */
  by_rating: RatingCounts
  na: number
  /** MYAC-1424: open rows waiting on a pending question — out of `assessable_total`,
   * shown next to N/A. Absent on a projection that predates it. */
  waiting?: number
  /** MYAC-1433 — nobody ever launched this referential: THE first-launch
   * predicate (the nudge, the launch dialog's badge and pre-selection, the
   * confirm title). Absent on an older projection ⇒ false (no nudge). */
  never_launched?: boolean
  /** IR-165 — the profile keys this adoption's plan gates on: a registry question
   * (`field`) bears on this referential only when its key is listed (or it asked).
   * `null`/absent ⇒ UNKNOWN (never planned, plan stamp reset by a relaunch, older
   * projection): the broad rule holds. `[]` ⇒ KNOWN, the plan gates on no key. */
  gate_keys?: string[] | null
  /**
   * The DENOMINATOR of the progress bar (MYAC-968): live items minus the N/A
   * ones, i.e. everything this adoption still has to rate. `by_rating.TOTAL` is
   * the matching numerator, so a finished adoption satisfies
   * `by_rating.TOTAL === assessable_total` and the gap during a run IS the
   * remaining work.
   *
   * OPTIONAL against the §4.0-4 evolution rule, deliberately: a server that
   * predates MYAC-968 omits it, and the bar falls back to indeterminate rather
   * than rendering a wrong ratio. It becomes required once dev/staging/prod all
   * run a projection that writes it.
   */
  assessable_total?: number
  stale_verdicts: number
  /** v2: ALWAYS present at adoption level, with all four blocks (§12.18). */
  themes: NavThemes
  chapters?: NavChapterNode[]
  checklist?: ChecklistCounts | null
}

/**
 * The track's MAC Score (MYAC-889) — THE dashboard gauge's source when the
 * project has active adoptions. `by_rating` is the pooled distribution behind
 * the score, `stale_verdicts` how much of it sits on a stale basis,
 * `progression` the trend between the last two settled score snapshots.
 * All optional against the §4.0-4 evolution rule (older projections omit them).
 */
export interface CombinedTrackScore {
  mac_score?: number | null
  by_rating?: RatingCounts
  stale_verdicts?: number
  progression?: 'increasing' | 'stable' | 'decreasing' | ''
}

/**
 * The referentials page's TAB counts, materialized per track (MYAC-889). Each was
 * a fold over `adoptions` here in the browser; the definition now lives in the
 * projection, and this page just renders it. `continuity_periodic` is a MAX over
 * the track's adoptions, not a sum — periodic obligations are scope-derived and
 * repeat identically on each. Optional: absent on docs projected before the field
 * existed, and an absent count renders NO number rather than a folded one.
 */
export interface TrackTabCounts {
  assessed?: number
  consistency_open?: number
  continuity_periodic?: number
  /** MYAC-1612 — the continuity rows still waiting for a person to date them. */
  continuity_unfilled?: number
}

export interface AssessmentNavView {
  view_version: number
  adoptions?: NavAdoption[]
  /** Per-track roll-up; a track is absent/null when nothing is adopted on it. */
  combined?: Partial<Record<ScopeTrack, CombinedTrackScore | null>>
  tab_counts?: Partial<Record<ScopeTrack, TrackTabCounts | null>>
  basis_hash?: string
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.3 — assessment_summary / assessment_task_groups (per-track singletons)
// ---------------------------------------------------------------------------

export interface SummaryGapEntry {
  requirement_uid: string
  rating?: string
}

export interface SummaryGapGroup {
  family: string
  priority?: string
  entries?: SummaryGapEntry[]
}

// v4 structured sections (2026-08-10) — mirrors CMS `SummarySections`: the
// componentized panel's data. The markdown stays the fallback surface for
// docs projected before the field existed.

export interface SummaryStripRow {
  referential_id: string
  label?: string
  /** ready | nearly ready | at risk | blocked — code-graded, never the LLM. */
  readiness?: string
  blocking?: number
  mac_score?: number | null
  mode?: string
}

export interface SummaryFindingRow {
  requirement_uid: string
  title?: string
  /** Printable clause code ("Art. 10(16)") — empty when machine-keyed. */
  code?: string
  referential_label?: string
  rating?: string
}

export interface SummaryGroupCard {
  label: string
  priority?: string
  tasks?: number
  narrative?: string
  findings?: SummaryFindingRow[]
  /** The group's task names — revealed when the card expands (v4.3). */
  task_names?: string[]
  more_tasks?: number
  uncovered?: number
}

export interface SummaryConstatRow {
  text: string
  /** The consistency task's name — the UI leads with it, `text` is the detail. */
  title?: string
  severity?: string
}

export interface SummaryOverdueRow {
  label: string
  due?: string
  months_late?: number | null
}

export interface SummarySections {
  brief?: string
  assessment_note?: string
  assessed_total?: number
  strips?: SummaryStripRow[]
  coverage_note?: string
  starving?: boolean
  /** Distinct anchored CLAUSES at the last summarize — not tasks. */
  open_actions?: number
  /** Open task rows, rejected excluded, at the last summarize (digest ≥ CMS#550). */
  open_rows?: number
  groups_total?: number
  groups?: SummaryGroupCard[]
  hidden_groups?: number
  hidden_tasks?: number
  consistency_note?: string
  consistency_open?: number
  consistency_critical?: number
  constats?: SummaryConstatRow[]
  continuity_note?: string
  deadlines_total?: number
  overdue_total?: number
  overdue?: SummaryOverdueRow[]
  completeness_note?: string
  dossier_pct?: number | null
  missing_total?: number
  missing?: string[]
}

export interface SummaryTrackPayload {
  markdown: string
  /** True when no verdict moved since the summary was derived (basis_hash). */
  fresh: boolean
  cadence_undefined?: number
  gap_groups?: SummaryGapGroup[]
  /** Absent on docs projected before v4 — the panel falls back to markdown. */
  sections?: SummarySections | null
}

/** An absent (or null) track means "no summary available for it" — render empty. */
export interface AssessmentSummaryView {
  view_version: number
  qms?: SummaryTrackPayload | null
  td?: SummaryTrackPayload | null
  basis_hash?: string
  updated_at?: Timestamp
}

export interface TaskGroup {
  label: string
  task_ids?: string[]
}

/**
 * One track's emergent work groups AND the plan's MATERIALIZED counts (v2,
 * §12.20) — the numbers the action-plan panel used to client-reduce out of its
 * lazily loaded rows (so badges read 0 until the tab was opened).
 */
export interface TaskGroupsTrackPayload {
  groups?: TaskGroup[]
  /** The grouping FACT's own number — the grouper's view of the plan. */
  task_count: number
  /**
   * DISTINCT anchored clauses with at least one OPEN remediation (two open
   * actions on the same clause count as one) — the tab badge's semantics.
   */
  open_actions: number
  open_rows: number
  resolved_rows: number
  /**
   * `{group_label: open_row_count}`. UNGROUPED open rows are NOT in the map
   * (Firestore forbids an empty field name and no label may be invented), so the
   * untriaged tail is `open_rows − sum(values)`.
   */
  by_group: Record<string, number>
}

export interface AssessmentTaskGroupsView {
  view_version: number
  qms?: TaskGroupsTrackPayload | null
  td?: TaskGroupsTrackPayload | null
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.4 — assessment_continuity (per-track singleton, NEW in §4.6b / §12.21)
// ---------------------------------------------------------------------------

/**
 * The schedule status the engine DERIVES — code-owned, never an invented default:
 * an unknown cadence or an unknown last-done stays reportable. `unknown` is the
 * StrEnum's own fallback member, so a future status degrades instead of crashing.
 */
export type ViewContinuityStatus =
  | 'ok'
  | 'due_soon'
  | 'overdue'
  // MYAC-1107 lot C — no execution yet AND none due yet: the first cycle is
  // anchored on a declared start (e.g. market placement); absence is NORMAL.
  | 'first_due'
  | 'last_done_unknown'
  | 'cadence_undefined'
  | 'per_entity_pending'
  // The condition is not met today (bound: code-decided; unbound: the
  // worker's evidence-cited judgment) — real, listed, nothing due.
  | 'conditionally_inactive'
  // The record census holds NO record of this activity — an audit finding in
  // its own right, never the same thing as `last_done_unknown`.
  | 'no_records'
  | 'unknown'

/** One cadence rule the documents state (the minimum anchored one drives the row). */
export interface ContinuityStatedRule {
  period_months?: number | null
  quote: string
}

/**
 * An OPEN plan action already anchored on this obligation. `id` is the
 * `assessment_remediations` row id, so the panel's `open-tasks` interaction
 * filters the plan by a direct row match — no join, no scan.
 */
export interface ContinuityTaskRef {
  id: string
  name: string
  adoption_id: string
}

/**
 * One periodic obligation of the scope, as the schedule table renders it.
 *
 * Dates are ISO `YYYY-MM-DD` STRINGS (day precision — a timestamp would invent a
 * time) and `''` means unknown, which the panel renders as '—' / "unknown".
 */
export interface ContinuityObligationRow {
  family: string
  /** MYAC-1107 lot B — the row's own identity (one row per stated rule ×
   *  source document); `''`/absent on docs projected before lot B. */
  row_key?: string
  /** The document stating THIS rule — the tree display's level-1 group. */
  source_document?: string
  /** The worker's VERBATIM description of what the activity consists of —
   *  copied from the source text; `''` on facts derived before 2026-08-20. */
  description?: string
  /** Structured name — fixed FIELDS, open VALUES (the worker's own words win). */
  action: string
  object_kind: string
  object_label: string
  /** Per-entity subject kind (e.g. a supplier register); `''` = scope-wide. */
  subject_type: string
  period_months?: number | null
  cadence_source: string
  /** Verbatim sentence stating the cadence, anchored in the client's own document. */
  cadence_quote: string
  quote_status: string
  stated_rules: ContinuityStatedRule[]
  last_done: string
  last_done_source: string
  /** MYAC-1642 — where `source_document` lives (the rule's document, which also
   *  states the cadence); absent when it did not resolve. */
  source_document_handle?: DocumentHandle | null
  /** MYAC-1642 — where the document dating `last_done` lives; absent when it did
   *  not resolve or no document dated it. */
  last_done_handle?: DocumentHandle | null
  /** MYAC-1107 lot C — the declared first-cycle start when no execution
   *  exists and an anchor key is settled; `''` otherwise. */
  anchor?: string
  /** Deterministic status explanation, composed by the engine from the very
   *  inputs the status derives from (raw English — engine strings). */
  status_reason?: string
  next_due: string
  status: ViewContinuityStatus
  per_entity: boolean
  open_tasks: number
  tasks: ContinuityTaskRef[]
  /** MYAC-1612 — what the targeted date search concluded for this row; absent when
   *  the row never needed it. */
  date_search?: ContinuityDateSearch | null
  /** MYAC-1612 — a person should date this row (no last execution, scope-wide,
   *  active): the « to fill » marker. The menu badge counts the same rule. */
  needs_date?: boolean
  /** MYAC-1612 — a person can set or correct this row's date from the page; the
   *  pencil shows only then. */
  date_editable?: boolean
  /** MYAC-1612 — a person can ask the engine to find this row's latest execution
   *  in the documents (dateable, not dated by a person, search switched on); the
   *  « find » control shows only then. */
  date_searchable?: boolean
}

export type ContinuityDateSearchStatus = 'found' | 'not_found' | 'failed'

export interface ContinuityDateSearch {
  status: ContinuityDateSearchStatus
  /** How many candidate documents the search read. */
  searched: number
  record_name?: string
  quote?: string
  /** The date another lane had given the row, when the search's date replaced it. */
  replaced?: string
}

/**
 * One track's schedule. `derived: false` = no `periodic_obligations` fact yet
 * (the panel's "not derived" notice); `as_of` is the date the statuses were
 * derived on — a time-derived view must state its own basis.
 */
export interface TrackContinuity {
  derived: boolean
  due_soon_days: number
  as_of: string
  obligations: ContinuityObligationRow[]
}

/** An absent (or null) track means "no schedule available for it". */
export interface AssessmentContinuityView {
  view_version: number
  qms?: TrackContinuity | null
  td?: TrackContinuity | null
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.10 — assessment_documents (the documents-used ledger, MYAC-890 phase b)
// ---------------------------------------------------------------------------

/** One document the assessment actually used; sources ∈ evidence | record |
 * consistency | continuity | fact. */
export interface DocumentAnchor {
  /** requirement | finding | obligation */
  kind: string
  ref: string
  label: string
  /** A requirement anchor's standard — its uid is unique only within one
   *  (MYAC-1396). Absent on documents projected before it. */
  adoption_id?: string
}

export interface DocumentUsedRow {
  filename: string
  sources: string[]
  /** Where the file lives, when the channels citing it agree on one (MYAC-1642). */
  document_handle?: DocumentHandle | null
  /** Capped server-side; anchors_more counts the hidden ones. */
  anchors?: DocumentAnchor[]
  anchors_more?: number
}

/** The requirement a gap answers to — code/title snapshots taken server-side, so
 * the chip renders and the origin dialog opens with no item read. */
export interface DocumentGapRequirement {
  uid: string
  code?: string
  title?: string
  /** The standard the uid belongs to (MYAC-1396); absent on older documents. */
  adoption_id?: string
  /** MYAC-1688 — where it sits in its standard's tree: the chapter code and the
   * section's `sub_key` (as `NavSubNode.sub_key`). Empty without an owner. */
  chapter?: string
  sub_key?: string
}

/** MYAC-1688 — how many gap rows name a requirement of one tree node: the
 * standard (`chapter` and `sub_key` empty), a chapter (`sub_key` empty) or a
 * section. Matched on the fields, never by parsing a key. */
export interface DocumentGapCount {
  adoption_id: string
  chapter?: string
  sub_key?: string
  documents: number
}

/** One named gap of the dossier. Which optional field is filled says which family
 * it belongs to: `candidate` (+`source`) → it exists at the manufacturer's,
 * `cited_in` → a dangling reference, neither → to produce. */
export interface DocumentGapRow {
  document: string
  candidate?: string
  source?: string
  cited_in?: string
  /** The open remediation task this gap is a `document_action` of (MYAC-1082) —
   * the row IS a projection of that task's plan, so the id is a free join: the
   * panel opens the task with it, and a task deep-links back to its row. Empty for
   * the TD presence-radar gaps, which have no task yet. */
  task_id?: string
  requirements?: DocumentGapRequirement[]
}

export interface TrackDocumentsUsed {
  documents: DocumentUsedRow[]
  /** Materialized gap counters (MYAC-889), deduplicated by document name across
   * the track: the other half of the completeness ratio. Absent on documents
   * written before the counters existed — read them tolerantly. */
  to_find?: number
  cited?: number
  missing?: number
  /** The gaps THEMSELVES (MYAC-889 lot B) — the panel's three lists, projected
   * where the track-wide union can actually be made (the browser only ever holds
   * the SELECTED adoption's items). Absent on docs written before they existed. */
  to_find_rows?: DocumentGapRow[]
  cited_rows?: DocumentGapRow[]
  missing_rows?: DocumentGapRow[]
  /** The Complétude tab's number, computed server-side where both halves of the
   * ratio live. `null`/absent = not measured; render nothing, never 0%. */
  completeness_pct?: number | null
  /** MYAC-1688 — the gap rows counted per tree node, in the pass that builds
   * them. `null`/absent = not computed; in a computed list, a node that is absent
   * has no gap. */
  gap_counts?: DocumentGapCount[] | null
}

/** Per-track singleton: EVERY document the assessment used, across all citation
 * channels (verdict evidence, execution records, consistency, continuity, facts). */
export interface AssessmentDocumentsView {
  view_version: number
  qms?: TrackDocumentsUsed | null
  td?: TrackDocumentsUsed | null
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.5 — assessment_items/rows/{aid}__{uid} (flat, filter-queried)
// ---------------------------------------------------------------------------

export interface ItemCriterionStatus {
  criterion: string
  /** Stable id of the obligation, echoing the requirement's authored criteria items. */
  key?: string
  /** The obligation's authored name — how the answer calls it (`criterion` if absent). */
  label?: string
  /** 'met' | 'partial' | 'absent' | 'contradicted' | 'not_evaluated'. */
  documented: string
  implemented: string
  note?: string
}

export interface ItemEvidence {
  filename: string
  section?: string
  excerpt: string
  justification?: string
  /** The criteria item this excerpt evidences (`ItemCriterionStatus.key`):
   *  the checklist puts each excerpt in front of its own line. Absent on
   *  verdicts written before the field, and on excerpts tied to no item. */
  criterion_key?: string
  /** `'human'` = customer-supplied (chat); `''`/absent = the assessor's own
   *  citation. Additive (MYAC-1051 Task 10) — rows written before this field
   *  existed carry no key at all, which reads as `''` (assessor). */
  origin?: string
  /** MYAC-1504 (additive): where the cited document lives, to open it at its
   *  source. Absent when the citation carries no id or points outside the
   *  project's own documents. */
  document_handle?: DocumentHandle | null
}

/** The current verdict only — no history, no superseded rows (spec §4.5). */
export interface ItemVerdict {
  rationale_md?: string
  rating_justification?: string
  /**
   * The composed half of the answer, per lens: one line per obligation (what the
   * arrangement provides, then what was examined for it) followed by the closing
   * constat. Written server-side, from the same rows the letter derives from — the
   * client renders it, it never rebuilds it. Empty on legacy verdicts with no constat.
   */
  answer_stage1_md?: string
  answer_stage2_md?: string
  criteria_status?: ItemCriterionStatus[]
  evidence?: ItemEvidence[]
}

export interface RadarCandidate {
  filename: string
  project_name?: string
  document_id?: string
}

/**
 * Presence-item enrichment stamped at conclude. `present` is emitted too (the
 * projection reports the satisfied case, not only the gap) — readers must treat
 * anything other than attach/produce as "nothing to do".
 */
export type RadarAction = 'attach' | 'produce' | 'present'

export interface ItemRadar {
  action: RadarAction
  candidates?: RadarCandidate[]
}

/** Continuity-item enrichment stamped at conclude. */
export interface ItemCadence {
  period_months?: number | null
  cadence_defined?: boolean
  cadence_source?: string
  /** ISO day (YYYY-MM-DD); '' = unknown — same convention as ContinuityObligationRow. */
  last_done?: string
}

export interface AssessmentItemRow {
  /** created_at of the CURRENT verdict — "last assessed" for the re-run tooltip. */
  judged_at?: Timestamp
  view_version: number
  adoption_id: string
  /**
   * v2 (§12.17): the adoption's stable DOCUMENT order — dense, 0-based, chapter →
   * sub-chapter → clause code in natural collation. Rows sort by `order` ALONE;
   * the front does NO code sorting. Recomputed on every rebuild, so it is a sort
   * index, never an identifier.
   */
  order: number
  /** `{chapter}|{sub}` — matches `NavSubNode.sub_key` exactly. */
  sub_key: string
  theme: ViewTheme
  requirement_uid: string
  code: string
  title: string
  /** Authored client-facing text. */
  description?: string
  status: ViewItemStatus
  applicability_justification?: string
  /** MYAC-1424: the pending question this OPEN row waits on — judged once it is
   * answered, out of the score meanwhile. Absent / '' = not waiting. */
  waiting_on?: string
  /** MYAC-1433 — the run entry that produced the current verdict ('' / absent
   * when none); equal to the track run's `entry_id` = judged in this run. */
  judged_run_id?: string
  /** Effective rating at the adoption's lens; '' on N/A items. */
  rating: string
  rating_stage1?: string
  rating_stage2?: string
  stale?: boolean
  grounded?: boolean
  /** MYAC-1688 — the backend's checklist state of this requirement, the one the
   * nav counts fold. Absent on rows projected before it. */
  checklist_state?: ChecklistState | null
  /** Absent on N/A items. */
  verdict?: ItemVerdict
  radar?: ItemRadar
  cadence?: ItemCadence
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.6 — assessment_remediations/rows/{remId} (the product-wide action plan)
// ---------------------------------------------------------------------------

/**
 * Code + title snapshot for the origin chip — full detail stays an on-demand item
 * read. v2 (§12.19) added `title`, so the plan renders its linked-requirement
 * badge with ZERO item reads. Both are `''` when the uid resolves to no live item
 * (a revision removed the clause while the task survived) — an empty label,
 * never a fabricated one.
 */
export interface RemediationAnchor {
  requirement_uid: string
  code?: string
  title?: string
  /** MYAC-1272 review round 2 (additive): the anchor's own display name.
   * Filled only when `code` is `''` — `derive_periodic_tasks` never fabricates
   * a code for the synthetic `obligation:{family}` anchor a periodic task
   * hangs off (no real clause governs it), so the family registry's display
   * name travels here instead. A chip reads `code || label || uid`. */
  label?: string
}

/**
 * A rejection that came back to review by itself when its period ended
 * (MYAC-1089) — CMS `ReviewReturn`, the same shape on a task, a blackboard entry
 * and a view row. Every value is as it stood the moment before the return.
 */
export interface ReviewReturned {
  /** When it came back, ISO-8601. */
  at: string
  /** The end of the period that ran out, ISO-8601. */
  rejected_until: string
  /** Who had rejected it, author grammar (`human:<uid>`); '' when unknown. */
  rejected_by: string
  /** The rejection comment it carried; '' when none. */
  reason: string
  /** MYAC-1639 — the set-aside reason it carried (`rejectionReason.ts`); '' when
   *  none, absent on a note written before it. */
  rejection_reason?: string
}

export interface AssessmentRemediationRow {
  view_version: number
  adoption_id: string
  referential_id: string
  name: string
  description_md?: string
  /** v3 (additive): the stake of not doing the work. */
  risk?: string
  /** v3 (additive): finding lines — GAP rows only (consistency lives in its own view). */
  constats?: string[]
  /** v3 (additive): per-document plan. The candidate fields are set (MYAC-890
   * phase b) when the radar found the document already existing at the
   * manufacturer's. */
  document_actions?: {
    document: string
    action_type: 'TO_CREATE' | 'TO_UPDATE' | 'TO_ATTACH'
    action: string
    /** MYAC-1092 — short imperative, `''` when the engine gave none. */
    title?: string
    /** MYAC-1092 — ready-to-copy text, `''` when nothing concrete can be drafted. */
    suggested_content?: string
    candidate_filename?: string
    candidate_source?: string
    /** dangling ref: the parent document that cites this absent one. */
    cited_in?: string
    /** MYAC-1504 (additive): the existing file this line is about (TO_UPDATE,
     *  TO_ATTACH); absent when unknown or ambiguous, always for TO_CREATE. */
    document_handle?: DocumentHandle | null
  }[]
  priority?: string
  /** '' | 'stage1' | 'stage2' | 'both' (code-derived). */
  stage?: string
  /** The 4C facet. */
  theme?: ViewTheme
  /** The old free-form content-topic label (ex-`theme`) — grouping fallback only. */
  topic?: string
  status: string
  documents?: string[]
  anchors?: RemediationAnchor[]
  /** Resolved from task_grouping at projection time (kills the client-side join). */
  group_label?: string
  // P5b (2026-08-10, additive) — the user-owned review state. Same vocabulary as
  // the legacy kanban's ProjectTask, so a future unified board merges by reading.
  review_decision?: '' | 'accepted' | 'rejected'
  /** The board column. */
  review_progress?: '' | 'todo' | 'in_progress' | 'done'
  review_assignee_uid?: string
  review_rejection_comment?: string
  /** MYAC-1639 — the set-aside reason (`rejectionReason.ts`); '' or absent = none,
   *  as on every row rejected before it. Only beside `review_decision: 'rejected'`. */
  review_rejection_reason?: string
  /** MYAC-1089 — end of the rejection's period, ISO-8601; '' or absent = no end.
   *  Non-empty only beside `review_decision: 'rejected'`. */
  review_rejected_until?: string
  /** MYAC-1089 — set on a row back to review (`review_decision: ''`) because its
   *  rejection period ran out; absent or null otherwise. */
  review_returned?: ReviewReturned | null
  /** OCC token — echoed back as `expected_seq` on the `remediation_edit` intent. */
  last_revision_seq?: number
  /** Consistency rows only: first constat, word-clipped — the finding chip's title. */
  finding_label?: string
  /** The engine's pending ask on an ACCEPTED row; absent = nothing pending. */
  proposed_change?: {
    kind: 'update' | 'closure' | ''
    name?: string
    description?: string
    risk?: string
    reason?: string
  }
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.6c — assessment_consistency/rows/{aid}__{n} (MYAC-930 doctrine completion)
// ---------------------------------------------------------------------------

/** One verbatim contradicting quote with its source (V1.5; legacy rows have none). */
export interface ConsistencyEvidenceRow {
  filename: string
  section?: string
  quote?: string
  justification?: string
  /** Where the quoted file lives, when the engine kept its id (MYAC-1642). */
  document_handle?: DocumentHandle | null
}

/**
 * One CONSTAT (individual contradiction — the granularity a human reads) of the
 * Cohérence tab, projected per-adoption from the consistency-category
 * remediations. `order` is the server-computed sort rank (open-first, severity,
 * constat) so the client never re-derives ordering.
 */
/** One requirement a Cohérence row cites, with its standard (MYAC-1396). */
export interface ConsistencyRequirement {
  uid: string
  adoption_id?: string
}

export interface AssessmentConsistencyRow {
  view_version: number
  adoption_id: string
  constat: string
  severity: string
  evidence: ConsistencyEvidenceRow[]
  documents: string[]
  requirement_uids: string[]
  /** The same requirements, each with its standard (MYAC-1396). The row's own
   *  `adoption_id` is the track's corpus sentinel, never a standard.
   *  `adoption_id` is `''` where the projection could not tell; the list is
   *  absent on older rows. */
  requirements?: ConsistencyRequirement[]
  status: string
  resolved_reason?: string | null
  /** The parent work item — the action-plan row the task badge points at. */
  task_id: string
  task_name: string
  /** v2.1 (MYAC-1042, additive): the finding's OWN presentation — an
   * observational title (no action verb) and a 1–2 sentence rationale (why
   * comparable, why contradictory; never names files). Absent on legacy rows
   * — fall back to `task_name`. */
  finding_title?: string
  finding_rationale?: string
  order: number
  updated_at?: Timestamp
}

// ---------------------------------------------------------------------------
// 4.7 — playbook/rows/{rowId} (MYAC-1051 — the Playbook page)
// ---------------------------------------------------------------------------

/**
 * The family's own few fields. FLAT and optional on purpose: `family` is DATA
 * (see `PlaybookRow`), so a family this front release has never heard of still
 * renders — it simply carries none of the keys below.
 *
 *  - `evidence`: `filename` (empty when the user asserted something with no
 *    document), `verified` (the passage was found verbatim), and the pair
 *    `identity` + `entry_id`, which the `playbook_delete` intent needs VERBATIM
 *    — the row id truncates the identity to 12 chars and cannot give it back.
 *  - `answer`: `field`, the profile key the question filled in.
 *  - `rejection`: `category` (the refused task's kind), `referential_id` and
 *    `requirement_codes` — WHAT the task served, so the row says which
 *    referential and which clauses the refusal bears on. Codes, not uids: they
 *    are the numbers the client reads on the requirement itself.
 */
export interface PlaybookRowDetail {
  filename?: string
  /**
   * The document's name AS THE CLIENT NAMED IT — MYAC-1272.
   *
   * `filename` is the STORED name, and everything this platform ingests is
   * converted to PDF, so a client's `SOP-QUA-021.docx` is stored — and was
   * shown — as `SOP-QUA-021.docx.pdf`: a double extension that names a file
   * they never had. CMS strips the conversion suffix (and only a suffix it can
   * explain: `scan.PDF` keeps its name, an unrecognised shape is returned
   * whole) and projects the result here.
   *
   * Additive, so ABSENT on every row projected before that landed — the
   * renderers fall back to `filename`, which is what they showed until now.
   * `""` on declared evidence, which has no file at all.
   *
   * Display only. `filename` stays the content key — it is half
   * `compute_evidence_entry_id`'s input — and the delete round trip addresses
   * an entry through the row's `ref`, never through either name.
   */
  document_name?: string
  /** Evidence only (MYAC-1642): where the cited file lives; absent when declared
   *  evidence has no file or the id did not resolve. */
  document_handle?: DocumentHandle | null
  verified?: boolean
  identity?: string
  entry_id?: string
  field?: string
  category?: string
  referential_id?: string
  requirement_codes?: string[]
  /**
   * The same requirements as `requirement_codes`, ALIGNED index by index — CMS
   * deduplicates both lists together, so `requirement_uids[i]` identifies
   * `requirement_codes[i]`.
   *
   * The code is what the client READS on the requirement; the uid is what
   * opens it. Absent on rows projected before MYAC-1256, and a row without it
   * still renders its codes — as plain, non-clickable chips.
   */
  requirement_uids?: string[]
}

/**
 * One standing input the CLIENT gave the engine (`views/playbook/rows/{rowId}`).
 *
 * One row per LIVE input: a deleted input is a deleted row (the audit trail is
 * the journal's, not this view's), which is why the page never removes a row
 * optimistically — the projection's next snapshot is what takes it down.
 *
 * `family` is a plain string, never a union: the CMS model states that families
 * are data so that family N+1 needs no front release. Unknown families render
 * generically — grouped under their own raw name, with the generic copy — and
 * they ARE actionable when the server says so: `actions` is projected per row
 * and is the only thing that decides a button (`hasAction`), so a family this
 * release cannot name is still removable.
 */
export interface PlaybookRow {
  view_version?: number
  /** "evidence" | "answer" | a future family. */
  family: string
  /** "requirement" | "profile_key" | "adoption" | "remediation" — WHAT the
   *  input is attached to. It drives the LINK, not the copy (see `LINKS`). */
  anchor_kind?: string
  anchor_key?: string
  anchor_label?: string
  /** "" when the input is scope-level rather than owned by one adoption. */
  adoption_id?: string
  content?: string
  detail?: PlaybookRowDetail
  /**
   * §4.1 — the ADDRESSING BLOCK, sent back verbatim in `playbook_delete`.
   *
   * Its shape belongs to the family (`{identity, entry_id}` for evidence,
   * `{remediation_id}` for a rejection, `{answer_id, request_id}` for an
   * answer) and is resolved by that family's deleter through a board lookup.
   * Untyped on purpose, on both sides of the wire: a union here would be a
   * second registry of families, drifting against the real one.
   *
   * Optional for ONE release — rows projected before this increment have none,
   * and evidence falls back to `detail` (see `rowRef`).
   */
  ref?: Record<string, string>
  /**
   * What the server says may be done to this row (`["delete"]` today).
   *
   * The client never derives an affordance from the family: the projection is
   * what knows whether a row is still addressable, so a missing action is a
   * missing button (`hasAction`). Absent on pre-increment rows.
   */
  actions?: string[]
  /** The verdict this input fed is flagged for re-review — the server's flag,
   *  never recomputed here. */
  stale?: boolean
  /** `human:{uid}` or `human:chat` — never rendered raw (see `playbookAuthorKey`). */
  author?: string
  created_at?: Timestamp
  updated_at?: Timestamp
  /**
   * When the engine last READ this input (MYAC-1257).
   *
   * Absent or `null` means the input is still waiting for the next assessment
   * — the normal state of anything given since the last run, and the state
   * every row projected before this increment is in. Never recomputed here:
   * the projection is the only thing that knows what the engine has read.
   */
  taken_into_account_at?: Timestamp
}
